/**
 * 用户模块Store
 * 迁移自 src/stores/user.ts
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserInfo } from '@/modules/auth/api'
import { 
  login as loginApi, 
  logout as logoutApi, 
  getUserPermissions as getUserPermissionsApi,
  refreshToken as refreshTokenApi, 
  updateProfile as updateProfileApi, 
  changePassword as changePasswordApi 
} from '@/modules/auth/api'
import { getCurrentUserProfile } from '../api'
import { permissionManager, type UserInfo as PermissionUserInfo } from '@/utils/permission'
import { PermissionCode, PermissionGroup } from '@/constants/permission-codes'
import { runApiBatch } from '@/shared/api/use-api-batch'
import type { UserPermissionsResponse } from '@/modules/auth/api'
import type { UserInfo as ProfileUserInfo } from '@/modules/auth/api'

export interface UserState {
  token: string
  userInfo: UserInfo | null
  permissions: Record<string, boolean>
  preferences: {
    language: string
    theme: 'light' | 'dark'
    notifications: {
      email: boolean
      push: boolean
      sms: boolean
    }
  }
}

export const useUserStore = defineStore('user', () => {
  const token = ref<string>(localStorage.getItem('token') || '')
  const userInfo = ref<UserInfo | null>(null)
  const permissions = ref<Record<string, boolean>>({})
  const preferences = ref({
    language: localStorage.getItem('user-language') || 'zh-CN',
    theme: (localStorage.getItem('user-theme') as 'light' | 'dark') || 'light',
    notifications: {
      email: JSON.parse(localStorage.getItem('user-notifications-email') || 'true'),
      push: JSON.parse(localStorage.getItem('user-notifications-push') || 'true'),
      sms: JSON.parse(localStorage.getItem('user-notifications-sms') || 'false')
    }
  })

  const user = computed(() => userInfo.value)
  const isLoggedIn = computed(() => !!token.value && !!userInfo.value)

  const isAdmin = computed(() => {
    void userInfo.value
    return permissionManager.isAdmin()
  })
  const canAccessVideo = computed(() => {
    void userInfo.value
    return permissionManager.hasAnyPermission([...PermissionGroup.VIDEO_PERMISSIONS])
  })
  const canAccessDocument = computed(() => {
    void userInfo.value
    return permissionManager.hasAnyPermission([...PermissionGroup.DOCUMENT_TRANSLATION_PERMISSIONS])
  })
  const canAccessSpeech = computed(() => {
    void userInfo.value
    return permissionManager.hasAnyPermission([...PermissionGroup.SPEECH_TRANSLATION_PERMISSIONS])
  })

  const hasPermission = computed(() => (permission: string) => permissions.value[permission] === true)

  const canAccessVideoTranslation = canAccessVideo
  const canAccessDocumentTranslation = canAccessDocument
  const canAccessSpeechTranslation = canAccessSpeech
  const canManageUsers = computed(() => {
    void userInfo.value
    return permissionManager.hasPermission(PermissionCode.SYSTEM_USER)
  })
  const canManageSystem = computed(() => {
    void userInfo.value
    return permissionManager.hasAnyPermission([...PermissionGroup.SYSTEM_MANAGEMENT_PERMISSIONS])
  })
  const canManageUsage = computed(() => {
    void userInfo.value
    return permissionManager.hasPermission(PermissionCode.SYSTEM_USAGE)
  })
  const canEditSystemConfig = computed(() => {
    void userInfo.value
    return permissionManager.hasPermission(PermissionCode.SYSTEM_CONFIG)
  })
  const canAssignTasks = computed(() => {
    void userInfo.value
    return permissionManager.hasPermission(PermissionCode.TASK_ASSIGN)
  })
  const canManageGroups = computed(() => {
    void userInfo.value
    return permissionManager.hasPermission(PermissionCode.SYSTEM_ORGANIZATION)
  })

  const hasAdminAccess = isAdmin

  const availableMenus = computed(() => {
    const menus: string[] = ['dashboard', 'profile']
    if (permissionManager.hasAnyPermission([...PermissionGroup.PROJECT_PERMISSIONS])) menus.push('project')
    if (permissionManager.hasAnyPermission([...PermissionGroup.TASK_PERMISSIONS])) menus.push('task')
    if (canAccessVideo.value) menus.push('video')
    if (canAccessDocument.value) menus.push('document')
    if (isAdmin.value || permissionManager.hasAnyPermission([...PermissionGroup.SYSTEM_MANAGEMENT_PERMISSIONS])) menus.push('system')
    if (canAccessSpeech.value) menus.push('speech-translation')
    if (permissionManager.hasAnyPermission([...PermissionGroup.CLASSROOM_PERMISSIONS])) menus.push('classroom')
    if (permissionManager.hasAnyPermission([...PermissionGroup.CLASSROOM_V3_PERMISSIONS])) menus.push('classroom-v3')
    return menus
  })

  const setToken = (newToken: string) => {
    token.value = newToken
    localStorage.setItem('token', newToken)
  }

  const clearToken = () => {
    token.value = ''
    userInfo.value = null
    permissions.value = {}
    localStorage.removeItem('token')
    permissionManager.clearUser()
  }

  const setUserInfo = (info: UserInfo | null) => {
    if (!info) {
      userInfo.value = null
      return
    }
    // 合并 permission_codes / permissions，供 permissionManager 严格按权限展示菜单与路由
    const merged = {
      ...info,
      permission_codes: (info as UserInfo & { permission_codes?: string[] }).permission_codes ?? [],
      role_codes: (info as UserInfo & { role_codes?: string[] }).role_codes ?? [],
      permissions: (info as UserInfo & { permissions?: Record<string, boolean> }).permissions ?? permissions.value ?? {}
    }
    userInfo.value = merged
    permissionManager.setUser(merged as PermissionUserInfo)
  }

  const setPermissions = (perms: Record<string, boolean>) => {
    permissions.value = perms
  }

  const updatePreferences = (newPreferences: Partial<typeof preferences.value>) => {
    preferences.value = { ...preferences.value, ...newPreferences }
    
    if (newPreferences.language) {
      localStorage.setItem('user-language', newPreferences.language)
    }
    if (newPreferences.theme) {
      localStorage.setItem('user-theme', newPreferences.theme)
    }
    if (newPreferences.notifications) {
      localStorage.setItem('user-notifications-email', JSON.stringify(newPreferences.notifications.email))
      localStorage.setItem('user-notifications-push', JSON.stringify(newPreferences.notifications.push))
      localStorage.setItem('user-notifications-sms', JSON.stringify(newPreferences.notifications.sms))
    }
  }

  const login = async (username: string, password: string) => {
    try {
      clearToken()
      
      const response = await loginApi({ username, password })
      
      setToken(response.access_token)
      setPermissions(response.permissions)
      // 登录响应中的 permission_codes 必须合并进 user，否则路由守卫会因无权限而 403
      const baseUser = {
        permission_codes: response.permission_codes ?? [],
        role_codes: response.role_codes ?? [],
        permissions: response.permissions ?? permissions.value ?? {}
      }
      try {
        const userDetail = await getCurrentUserProfile()
        setUserInfo({ ...userDetail, ...baseUser })
        if (import.meta.env.DEV) {
          console.log('登录成功，完整用户信息（包含group）:', userDetail)
        }
      } catch (detailError) {
        if (import.meta.env.DEV) {
          console.warn('获取用户详细信息失败，使用基本信息:', detailError)
        }
        setUserInfo({ ...response.user, ...baseUser })
      }
      
      return response
    } catch (error) {
      clearToken()
      throw error
    }
  }

  const logout = async () => {
    try {
      await logoutApi()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Logout API failed:', error)
      }
    } finally {
      clearToken()
    }
  }

  // 防止重复请求：并发调用共享同一 in-flight Promise
  let currentUserInflight: Promise<{
    access_token: string
    token_type: string
    user: UserInfo
    permissions: Record<string, boolean>
  }> | null = null

  const getCurrentUser = async () => {
    if (userInfo.value) {
      return {
        access_token: token.value,
        token_type: 'bearer',
        user: userInfo.value,
        permissions: permissions.value,
      }
    }

    if (currentUserInflight) {
      return currentUserInflight
    }

    currentUserInflight = (async () => {
      try {
        const results = await runApiBatch<unknown>([
          (ctx) => getCurrentUserProfile(ctx.requestConfig),
          (ctx) => getUserPermissionsApi(ctx.requestConfig),
        ])

        const userResult = results[0]
        const permResult = results[1]
        if (userResult.status === 'rejected') {
          throw userResult.reason
        }
        if (permResult.status === 'rejected') {
          throw permResult.reason
        }

        const userDetail = userResult.value as ProfileUserInfo
        const permResponse = permResult.value as UserPermissionsResponse
        setPermissions(permResponse.permissions)
        const mergedUser = {
          ...userDetail,
          permission_codes:
            permResponse.permission_codes ??
            (userDetail as ProfileUserInfo & { permission_codes?: string[] }).permission_codes ??
            [],
          role_codes:
            permResponse.role_codes ??
            (userDetail as ProfileUserInfo & { role_codes?: string[] }).role_codes ??
            [],
          permissions:
            permResponse.permissions ??
            (userDetail as ProfileUserInfo & { permissions?: Record<string, boolean> }).permissions ??
            permissions.value ??
            {},
        }
        setUserInfo(mergedUser)

        return {
          access_token: token.value,
          token_type: 'bearer',
          user: mergedUser,
          permissions: permResponse.permissions,
        }
      } catch (error) {
        clearToken()
        throw error
      } finally {
        currentUserInflight = null
      }
    })()

    return currentUserInflight
  }

  const refreshToken = async () => {
    try {
      const response = await refreshTokenApi()
      setToken(response.access_token)
      setPermissions(response.permissions)
      setUserInfo({
        ...response.user,
        permission_codes: (response as { permission_codes?: string[] }).permission_codes ?? (response.user as { permission_codes?: string[] }).permission_codes ?? [],
        role_codes: (response as { role_codes?: string[] }).role_codes ?? (response.user as { role_codes?: string[] }).role_codes ?? [],
        permissions: response.permissions ?? permissions.value ?? {}
      })
      return response
    } catch (error) {
      clearToken()
      throw error
    }
  }

  const updateProfile = async (profileData: Partial<UserInfo>) => {
    try {
      const updatedUserInfo = await updateProfileApi(profileData)
      setUserInfo(updatedUserInfo)
      return updatedUserInfo
    } catch (error) {
      throw error
    }
  }

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      await changePasswordApi({ oldPassword, newPassword })
    } catch (error) {
      throw error
    }
  }

  const initializeFromStorage = () => {
    const storedToken = localStorage.getItem('token')
    if (storedToken) {
      token.value = storedToken
      getCurrentUser().catch(() => {
        clearToken()
      })
    }
  }

  const getUserInfo = () => {
    return getCurrentUser()
  }

  return {
    token,
    userInfo,
    permissions,
    preferences,
    user,
    isLoggedIn,
    isAdmin,
    hasPermission,
    canAccessVideoTranslation,
    canAccessDocumentTranslation,
    canManageUsers,
    canManageSystem,
    canManageUsage,
    canEditSystemConfig,
    canAssignTasks,
    hasAdminAccess,
    canManageGroups,
    availableMenus,
    setToken,
    clearToken,
    setUserInfo,
    setPermissions,
    updatePreferences,
    login,
    logout,
    getCurrentUser,
    getUserInfo,
    refreshToken,
    updateProfile,
    changePassword,
    initializeFromStorage
  }
})

